import React from "react";
import Seo from "./Seo";

/**
 * Services page with consistent professional design.
 */
export default function Services() {
  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: "DevSecOps, Security Engineering & Software Development",
    provider: { "@type": "Organization", name: "LinuxSecOps" },
    areaServed: "Global",
    description:
      "DevSecOps enablement, Security Engineering, and software development services.",
  };

  return (
    <div className="services-container">
      <Seo
        title="Services - LinuxSecOps"
        description="DevSecOps pipelines, policy-as-code, application security, cloud posture, and software development services."
        path="/services"
        jsonLd={serviceJsonLd}
      />

      {/* Hero Section */}
      <section className="services-hero">
        <div className="services-hero-content">
          <div className="hero-badge">Services</div>
          <h1 className="services-hero-title">
            Security Engineering
            <span className="services-hero-accent"> & Development</span>
          </h1>
          <p className="services-hero-description">
            Practical outcomes: hardened pipelines, measurable security posture,
            and maintainable systems built with modern best practices.
          </p>
        </div>
      </section>

      {/* Services Grid */}
      <div className="services-content">
        {/* DevSecOps */}
        <section className="service-block">
          <div className="service-header">
            <div className="service-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
              </svg>
            </div>
            <div>
              <h2 className="service-title">DevSecOps Enablement</h2>
              <p className="service-subtitle">Secure your delivery pipelines</p>
            </div>
          </div>
          <div className="service-features">
            <div className="service-feature">
              <div className="feature-check">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <span>CI/CD hardening, secrets management, SBOM/SCA integration</span>
            </div>
            <div className="service-feature">
              <div className="feature-check">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <span>Policy-as-code and compliance gates (NIST/ISO)</span>
            </div>
            <div className="service-feature">
              <div className="feature-check">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <span>Artifact signing, provenance, and supply chain security</span>
            </div>
            <div className="service-feature">
              <div className="feature-check">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <span>Roll-out via sprints with validation artifacts</span>
            </div>
          </div>
        </section>

        {/* Security Engineering */}
        <section className="service-block">
          <div className="service-header">
            <div className="service-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <line x1="2" y1="12" x2="22" y2="12"/>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/>
              </svg>
            </div>
            <div>
              <h2 className="service-title">Security Engineering</h2>
              <p className="service-subtitle">Comprehensive security program</p>
            </div>
          </div>
          <div className="service-features">
            <div className="service-feature">
              <div className="feature-check">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <span>Application security: OWASP Top 10, SAST/SCA/DAST</span>
            </div>
            <div className="service-feature">
              <div className="feature-check">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <span>Cloud posture baselines and guardrails (AWS/Azure/GCP)</span>
            </div>
            <div className="service-feature">
              <div className="feature-check">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <span>Detection engineering: MITRE mapping, CEF/STIX, Wazuh</span>
            </div>
            <div className="service-feature">
              <div className="feature-check">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="20 6 9 17 4 12"/>
                </svg>
              </div>
              <span>Threat modeling and security architecture review</span>
            </div>
          </div>
        </section>

        {/* Software Development */}
        <section className="service-block service-block-wide">
          <div className="service-header">
            <div className="service-icon">
              <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="16 18 22 12 16 6"/>
                <polyline points="8 6 2 12 8 18"/>
              </svg>
            </div>
            <div>
              <h2 className="service-title">Software Development</h2>
              <p className="service-subtitle">Full-stack development services</p>
            </div>
          </div>
          <div className="dev-services-grid">
            <div className="dev-service-card">
              <h3>Frontend - React & Next.js</h3>
              <ul>
                <li>Modular architecture with design systems</li>
                <li>State: Redux Toolkit, Zustand, React Query</li>
                <li>Next.js: SSR/SSG/ISR, App Router</li>
                <li>Performance: Core Web Vitals optimization</li>
                <li>E2E testing with Playwright</li>
              </ul>
            </div>
            <div className="dev-service-card">
              <h3>Mobile - React Native</h3>
              <ul>
                <li>Monorepo with shared modules</li>
                <li>Push notifications, deep links, offline-first</li>
                <li>EAS/Fastlane CI/CD automation</li>
                <li>Crash reporting: Sentry/Crashlytics</li>
              </ul>
            </div>
            <div className="dev-service-card">
              <h3>Backend - Python</h3>
              <ul>
                <li>FastAPI/Django API development</li>
                <li>Auth: OAuth2/OIDC, JWT, RBAC</li>
                <li>PostgreSQL, Redis, message queues</li>
                <li>Observability: Prometheus, OpenTelemetry</li>
              </ul>
            </div>
          </div>
        </section>

        {/* Engagement Model */}
        <section className="engagement-section">
          <h2 className="engagement-title">Engagement Model</h2>
          <div className="engagement-grid">
            <div className="engagement-card">
              <div className="engagement-duration">2-4 weeks</div>
              <h3>Assessment</h3>
              <p>Gap analysis, prioritization, implementation plan</p>
            </div>
            <div className="engagement-card engagement-card-featured">
              <div className="engagement-duration">4-12 weeks</div>
              <h3>Implementation</h3>
              <p>Hardening, automation, integrations, documentation</p>
            </div>
            <div className="engagement-card">
              <div className="engagement-duration">Ongoing</div>
              <h3>Managed Services</h3>
              <p>Monitoring, detection rules, runbooks, improvements</p>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="services-cta">
          <h2 className="services-cta-title">Ready to get started?</h2>
          <p className="services-cta-description">
            Let's discuss your security and development requirements.
          </p>
          <a href="mailto:linuxsecops@proton.me" className="btn-primary">
            Contact for Quote
          </a>
        </section>
      </div>
    </div>
  );
}
